import { useQuery } from '@apollo/client';
import { useAuth } from '@contexts/AuthContext';
import { useProId } from '@contexts/ProIdContext';
import { SEARCH_JOB_APPLICATION_FILTERS_PRO, SEARCH_JOB_APPLICATION_FILTERS_UPERCUT } from '@gql/jobs';
import { IJobApplicationDetailFilter } from '@modules/jobApplications/JobApplicationFilter';

export type JobApplicationFilterOption = {
    value: string;
    label: string;
    count: number;
    groupLabel?: string;
};

export type JobApplicationFilterRange = {
    min: number | null;
    max: number | null;
};

export type AvailableJobApplicationFilters = {
    proIds: JobApplicationFilterOption[];
    statuses: JobApplicationFilterOption[];
    cities: JobApplicationFilterOption[];
    jobOfferCities: JobApplicationFilterOption[];
    sectorIds: JobApplicationFilterOption[];
    specialties: JobApplicationFilterOption[];
    diplomaTypes: JobApplicationFilterOption[];
    contractTypes: JobApplicationFilterOption[];
    professions: JobApplicationFilterOption[];
    salaryRange: JobApplicationFilterRange;
    yearsOfExperience?: JobApplicationFilterRange;
};

const DEFAULT_AVAILABLE_FILTERS: AvailableJobApplicationFilters = {
    proIds: [],
    statuses: [],
    cities: [],
    jobOfferCities: [],
    sectorIds: [],
    specialties: [],
    diplomaTypes: [],
    contractTypes: [],
    professions: [],
    salaryRange: { min: null, max: null },
    yearsOfExperience: { min: null, max: null },
};

/**
 * Fetches cascading filter options with counts for job applications.
 * Automatically selects the appropriate query based on the user's role.
 */
export const useJobApplicationFilters = (
    filter: IJobApplicationDetailFilter = {},
    query: string = '',
) => {
    const { user, isUpercut } = useAuth();
    const { isProIdRequired } = useProId();

    const isUpercutView = isUpercut && !isProIdRequired;
    const queryToUse = isUpercutView
        ? SEARCH_JOB_APPLICATION_FILTERS_UPERCUT
        : SEARCH_JOB_APPLICATION_FILTERS_PRO;
    const queryKey = isUpercutView
        ? 'searchJobApplicationFiltersUpercut'
        : 'searchJobApplicationFiltersPro';

    const { data, loading } = useQuery(queryToUse, {
        variables: {
            query,
            filter: {
                ...filter,
                yearsOfExperience:
                    filter.yearsOfExperience?.min != null || filter.yearsOfExperience?.max != null
                        ? { min: filter.yearsOfExperience.min, max: filter.yearsOfExperience.max }
                        : undefined,                locations: filter.locations?.map(o => ({
                    city: (o as any).value || '',
                    postalCode: String((o as any).address?.postalCode ?? ''),
                })),
                jobOfferLocations: filter.jobOfferLocations?.map(o => ({
                    city: (o as any).value || '',
                    postalCode: String((o as any).address?.postalCode ?? ''),
                })),            },
        },
        skip: !user,
        fetchPolicy: 'network-only',
        notifyOnNetworkStatusChange: true,
    });

    return {
        availableFilters: (data?.[queryKey] as AvailableJobApplicationFilters) ?? DEFAULT_AVAILABLE_FILTERS,
        filtersLoading: loading,
    };
};
